import { useState, useCallback } from 'react';
import { razorpayService } from '../services/razorpayService';
import { useInitiatePayment, usePaymentSuccess, usePaymentFailure } from './useTallyQueries';
import type { RazorpayConfig, RazorpayResponse, RazorpayError } from '../types';

interface PaymentState {
  isProcessing: boolean;
  paymentRequestId: string | null;
  razorpayOrderId: string | null;
  error: RazorpayError | null;
  isSuccess: boolean;
}

interface UsePaymentReturn {
  paymentState: PaymentState;
  initiatePayment: (flatId: string, chargeId: string, amount: number) => Promise<void>;
  clearError: () => void;
}

export const usePayment = (): UsePaymentReturn => {
  const [paymentState, setPaymentState] = useState<PaymentState>({
    isProcessing: false,
    paymentRequestId: null,
    razorpayOrderId: null,
    error: null,
    isSuccess: false
  });

  const initiatePaymentMutation = useInitiatePayment();
  const paymentSuccessMutation = usePaymentSuccess();
  const paymentFailureMutation = usePaymentFailure();

  const clearError = useCallback(() => {
    setPaymentState(prev => ({ ...prev, error: null, isSuccess: false }));
  }, []);

  const initiatePayment = useCallback(async (flatId: string, chargeId: string, amount: number) => {
    try {
      console.log('🎯 Starting payment flow:', { flatId, chargeId, amount });
      
      // Reset state
      setPaymentState({
        isProcessing: true,
        paymentRequestId: null,
        razorpayOrderId: null,
        error: null,
        isSuccess: false
      });

      // Step 1: Initiate payment with backend
      console.log('📤 Initiating payment with backend');
      const response = await initiatePaymentMutation.mutateAsync({
        flatId,
        chargeId,
        amount
      });

      console.log('✅ Payment initiated successfully:', response);
      
      // Update state with payment details
      setPaymentState(prev => ({
        ...prev,
        paymentRequestId: response.paymentRequestId,
        razorpayOrderId: response.razorpayOrderId
      }));

      // Step 2: Configure Razorpay
      const razorpayConfig: RazorpayConfig = {
        key: response.razorpayKeyId,
        amount: amount * 100, // Convert to paise
        orderId: response.razorpayOrderId,
        currency: 'INR',
        name: 'Amanora Payment',
        description: `Payment for charge ${chargeId}`,
        prefill: {
          name: 'User', // You can pass actual user details
          email: 'user@example.com',
          contact: '9999999999'
        }
      };

      // Step 3: Open Razorpay modal
      console.log('🔓 Opening Razorpay modal');
      await razorpayService.initiatePayment(razorpayConfig, {
        onSuccess: async (razorpayResponse: RazorpayResponse) => {
          console.log('🎉 Payment succeeded, confirming with backend');
          
          try {
            // Confirm payment with backend
            await paymentSuccessMutation.mutateAsync({
              razorpay_order_id: razorpayResponse.razorpay_order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature,
              paymentRequestId: response.paymentRequestId
            });

            console.log('✅ Payment confirmed successfully');
            
            // Set success state - payment complete
            setPaymentState({
              isProcessing: false,
              paymentRequestId: response.paymentRequestId,
              razorpayOrderId: null,
              error: null,
              isSuccess: true
            });

          } catch (confirmError) {
            console.error('❌ Failed to confirm payment:', confirmError);
            setPaymentState(prev => ({
              ...prev,
              isProcessing: false,
              isSuccess: false,
              error: {
                error: {
                  code: 'CONFIRMATION_FAILED',
                  description: 'Payment succeeded but confirmation failed',
                  source: 'backend',
                  step: 'confirmation',
                  reason: 'api_error',
                  metadata: {}
                }
              }
            }));
          }
        },

        onError: async (error: RazorpayError) => {
          console.log('❌ Payment method failed:', error);
          
          // Report failure to backend but don't update UI state yet
          // The modal is still open and user can try other payment methods
          try {
            await paymentFailureMutation.mutateAsync({
              razorpay_order_id: response.razorpayOrderId,
              razorpay_payment_id: error.error.metadata?.payment_id || '',
              error: {
                code: error.error.code,
                description: error.error.description,
                source: error.error.source,
                step: error.error.step || 'payment'
              }
            });
          } catch (reportError) {
            console.error('Failed to report payment failure:', reportError);
          }

          // Don't set error state here - let user try other payment methods
          // Error will only be shown if modal is dismissed without success
        },

        onDismiss: async () => {
          console.log('❌ Payment modal dismissed');
          
          try {
            // Report cancellation to backend
            await paymentFailureMutation.mutateAsync({
              razorpay_order_id: response.razorpayOrderId,
              razorpay_payment_id: '',
              error: {
                code: 'PAYMENT_CANCELLED',
                description: 'Payment cancelled by user',
                source: 'customer',
                step: 'modal'
              }
            });
          } catch (reportError) {
            console.error('Failed to report payment cancellation:', reportError);
          }

          // Show error modal when dismissed without success
          setPaymentState({
            isProcessing: false,
            paymentRequestId: null,
            razorpayOrderId: null,
            error: {
              error: {
                code: 'PAYMENT_CANCELLED',
                description: 'Payment was cancelled. You can try again when ready.',
                source: 'customer',
                step: 'modal',
                reason: 'user_cancelled',
                metadata: {}
              }
            },
            isSuccess: false
          });
        }
      });

    } catch (error) {
      console.error('💥 Payment initiation failed:', error);
      
      setPaymentState({
        isProcessing: false,
        paymentRequestId: null,
        razorpayOrderId: null,
        error: {
          error: {
            code: 'INITIATION_FAILED',
            description: error instanceof Error ? error.message : 'Payment initiation failed',
            source: 'frontend',
            step: 'initiation',
            reason: 'api_error',
            metadata: {}
          }
        },
        isSuccess: false
      });
    }
  }, [initiatePaymentMutation, paymentSuccessMutation, paymentFailureMutation]);

  return {
    paymentState,
    initiatePayment,
    clearError
  };
};